import React from 'react';
import { useLocation } from 'react-router-dom';
import { reportError } from '@/services/errorReporter';

/**
 * The top-level error boundary (ADO #575).
 *
 * A render error anywhere below used to unmount the whole tree and leave a
 * blank page. Now the learner sees what happened and a Reload button, and the
 * error is reported (kind 'react.render') to the admin console's Errors page.
 * A failed lazy page chunk after a deploy lands here too, and Reload is the fix.
 *
 * `resetKey`: when it changes, the boundary tries its children again — the
 * route-level boundary passes the path, so going Back or following a link
 * leaves the error behind instead of keeping it on every page.
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportError('react.render', error, {
      ...(info?.componentStack ? { componentStack: String(info.componentStack).slice(0, 1000) } : {}),
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <ErrorFallback />;
  }
}

export function ErrorFallback() {
  const reload = () => {
    try { window.location.reload(); } catch { /* nothing more to do */ }
  };
  return (
    <div
      role="alert"
      data-testid="app-error-fallback"
      className="min-h-[60vh] flex items-center justify-center px-4 py-10 bg-white text-slate-800 dark:bg-slate-950 dark:text-slate-100"
    >
      <div className="max-w-md w-full text-center">
        <h1 className="text-xl font-semibold mb-2">Something went wrong on this page</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          It is not something you did. We have been told about it. Reloading the page usually fixes it.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={reload}
            className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            Reload
          </button>
          <a
            href="/dashboard"
            className="px-4 py-2 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-900"
          >
            Go to dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

// Inside the router: the path is the reset key.
export function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
}

export default AppErrorBoundary;
